import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { ModuleService, CreateModuleRequest } from '../../../services/module.service';
import { MenuGroupService } from '../../../services/menu-group.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';
import { FeathericonsModule } from '../../../icons/feathericons/feathericons.module';

@Component({
    selector: 'app-create-module-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatSnackBarModule,
        MatCardModule,
        FeathericonsModule,
    ],
    templateUrl: './create-module-page.component.html',
    styleUrl: './create-module-page.component.scss'
})
export class CreateModulePageComponent implements OnInit {
    moduleForm: FormGroup;
    isLoading = false;
    availableIcons: string[] = [];
    menuGroups: { nombre: string; orden: number }[] = [];

    constructor(
        // eslint-disable-next-line no-unused-vars
        private fb: FormBuilder,
        // eslint-disable-next-line no-unused-vars
        private moduleService: ModuleService,
        // eslint-disable-next-line no-unused-vars
        private menuGroupService: MenuGroupService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService
    ) {
        this.moduleForm = this.fb.group({
            code: ['', [Validators.required, Validators.maxLength(50)]],
            name: ['', [Validators.required, Validators.maxLength(100)]],
            description: ['', [Validators.maxLength(255)]],
            route: ['/under-construction', [Validators.maxLength(100)]],
            icon: ['', [Validators.maxLength(50)]],
            order: [0, [Validators.required, Validators.min(0)]],
            menuGroup: ['', [Validators.maxLength(50)]],
            isActive: [true]
        });
    }

    ngOnInit(): void {
        this.initializeAvailableIcons();
        this.loadMenuGroups();
    }

    private loadMenuGroups(): void {
        this.menuGroupService.getMenuGroups().subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    this.menuGroups = res.data
                        .slice()
                        .sort((a, b) => a.orden - b.orden)
                        .map((g) => ({ nombre: g.nombre, orden: g.orden }));
                    this.menuGroups.push({ nombre: 'ADMINISTRACIÓN', orden: 9999 });
                }
            },
        });
    }

    /**
     * Inicializa la lista de íconos disponibles en formato kebab-case
     * basándose en los íconos definidos en FeathericonsModule
     */
    private initializeAvailableIcons(): void {
        const iconNames = [
            'activity', 'camera', 'heart', 'lock', 'table', 'send', 'thumbs-up', 'edit',
            'github', 'align-left', 'alert-triangle', 'file-minus', 'crosshair', 'twitch',
            'plus', 'youtube', 'file', 'folder', 'trash', 'link', 'meh', 'facebook', 'twitter',
            'user-plus', 'codepen', 'smile', 'settings', 'linkedin', 'dribbble', 'x',
            'shopping-bag', 'octagon', 'map-pin', 'command', 'file-text', 'copy', 'flag',
            'list', 'share-2', 'play-circle', 'user-check', 'book-open', 'key', 'search',
            'check', 'coffee', 'code', 'star', 'headphones', 'moon', 'briefcase',
            'message-square', 'users', 'map', 'bar-chart-2', 'info', 'calendar', 'loader',
            'alert-circle', 'more-vertical', 'minimize', 'bell', 'phone', 'edit-3', 'clock',
            'tag', 'globe', 'trash-2', 'dollar-sign', 'percent', 'database', 'check-circle',
            'shopping-cart', 'sliders', 'layers', 'user', 'mail', 'pie-chart', 'home',
            'trending-up', 'trending-down', 'log-out', 'truck', 'droplet',
            'mat:local_gas_station'
        ];
        this.availableIcons = iconNames.sort();
    }

    getErrorMessage(fieldName: string): string {
        const control = this.moduleForm.get(fieldName);
        if (!control) return '';

        if (control.hasError('required')) {
            return 'Este campo es requerido';
        }

        if (control.hasError('maxlength')) {
            const requiredLength = control.errors?.['maxlength']?.requiredLength;
            return `Máximo ${requiredLength} caracteres`;
        }

        if (control.hasError('min')) {
            return 'El valor debe ser mayor o igual a 0';
        }

        return 'Campo inválido';
    }

    markFormGroupTouched(): void {
        Object.keys(this.moduleForm.controls).forEach(key => {
            const control = this.moduleForm.get(key);
            control?.markAsTouched();
        });
    }

    onSubmit(): void {
        if (this.moduleForm.valid) {
            this.isLoading = true;
            
            const formData = this.moduleForm.value;
            const moduleData: CreateModuleRequest = {
                code: formData.code.trim(),
                name: formData.name.trim(),
                description: formData.description?.trim() || undefined,
                route: formData.route?.trim() || undefined,
                icon: formData.icon?.trim() || undefined,
                order: formData.order || 0,
                menuGroup: formData.menuGroup === null || formData.menuGroup === undefined 
                    ? null 
                    : (formData.menuGroup.trim() !== '' ? formData.menuGroup.trim() : null),
                isActive: formData.isActive !== undefined ? formData.isActive : true
            };

            this.moduleService.createModule(moduleData)
                .pipe(
                    catchError(error => {
                        this.logger.error('Error al crear módulo', error as Error, 'CreateModulePageComponent');
                        const errorMessage = error.error?.message || 'Error al crear el módulo';
                        this.snackBar.open(errorMessage, 'Cerrar', {
                            duration: 5000,
                            panelClass: ['error-snackbar']
                        });
                        return of(null);
                    }),
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe(response => {
                    if (response && response.success) {
                        // Notificar que los módulos han cambiado
                        this.moduleService.notifyModulesChanged();
                        
                        this.snackBar.open('Módulo creado exitosamente', 'Cerrar', {
                            duration: 3000,
                            panelClass: ['success-snackbar']
                        });
                        this.router.navigate(['/modules']);
                    }
                });
        } else {
            this.markFormGroupTouched();
        }
    }

    onCancel(): void {
        this.router.navigate(['/modules']);
    }
}

